/**
 * Ghost Verification Service
 * Handles ghost verification data fetching
 * Mock/Real switching is handled at Service layer
 */

import { IS_MOCK, MOCK_DELAY } from '../../constants/config';
import { apiService } from '../api/ApiService';
import { mockGhostApplications } from '../mock/mockGhostVerification';
import { generateGhostApplicationDetail } from '../mock/mockGhostVerificationDetail';
import { delay } from '../mock/mockData';
import { parseDate, formatDate, formatDateTime } from '../../utils/dateUtils';
import { getCallSignDisplayName, getStateName } from '../../utils/generalUtils';
import type {
  GhostApplication,
  GhostApplicationDetail,
  GhostVerificationFilter,
  GhostVerificationStatus,
  PaginatedGhostApplications,
  BondVerificationStatus,
} from '../../types/ghostVerification';

class GhostVerificationService {
  private static sanitizeGhostApplication(application: GhostApplication): GhostApplication {
    return {
      ...application,
      applicantName: getCallSignDisplayName(application.callSign, 'Ghost'),
    };
  }

  private static sanitizeGhostApplicationDetail(
    application: GhostApplicationDetail
  ): GhostApplicationDetail {
    const applicantName = getCallSignDisplayName(application.callSign, 'Ghost');

    return {
      ...application,
      applicantName,
      identityInfo: application.identityInfo
        ? {
            ...application.identityInfo,
            fullName: applicantName,
          }
        : application.identityInfo,
    };
  }

  /**
   * Convert API ghost application to internal format
   */
  private static convertGhostApplicationFromApi(apiItem: any): GhostApplication {
    return GhostVerificationService.sanitizeGhostApplication({
      id: apiItem.id,
      ghostId: apiItem.ghostId,
      email: apiItem.email,
      firstName: apiItem.firstName,
      lastName: apiItem.lastName,
      callSign: apiItem.callSign,
      type: apiItem.type,
      verificationStatus: apiItem.verificationStatus as GhostVerificationStatus,
      ghostTier: apiItem.ghostTier,
      submittedOn: apiItem.submittedOn,
      applicantName: getCallSignDisplayName(apiItem.callSign, 'Ghost'),
    });
  }

  private static convertGhostApplicationDetailFromApi(apiItem: any): GhostApplicationDetail {
    // Get basic application fields using the existing converter
    const basicApplication = GhostVerificationService.convertGhostApplicationFromApi(apiItem);

    return GhostVerificationService.sanitizeGhostApplicationDetail({
      ...basicApplication,

      // Additional detail fields
      email: apiItem.email,
      phoneNumber: apiItem.phone,
      verificationNotes: apiItem.notes || 'No verification notes available.',
      type: apiItem.type,

      // PI License Information
      piLicenseInfo: {
        licenseNumber: apiItem.licenseNumber,
        issuingState: apiItem.issuingState,
        issuingStateFull: apiItem.issuingState ? getStateName(apiItem.issuingState) : undefined,
        licenseExpiration: apiItem.licenseExpiration ? formatDate(apiItem.licenseExpiration) : undefined,
        licenseDocument: apiItem.licenseDocument,
        yearsOfExperience: apiItem.yearsOfExperience,
        skills: apiItem.skills,
      },

      // Bond Information
      bondInfo: apiItem.bondDocument ? {
        bondNumber: apiItem.licenseNumber,
        bondCompanyName: apiItem.bondCompanyName,
        naicNumber: apiItem.naicNumber,
        bondAmount: apiItem.insuredAmount,
        bondExpiryDate: apiItem.bondExpirationDate ? parseDate(apiItem.bondExpirationDate) || undefined : undefined,
        bondExpiryDateText: apiItem.bondExpirationDate ? formatDate(apiItem.bondExpirationDate) : undefined,
        bondDocument: {
          id: 'bond-doc',
          url: apiItem.bondDocument.url,
          fileName: apiItem.bondDocument.name,
          uploadedAt: new Date(),
          uploadedAtText: formatDateTime(new Date()),
          mimeType: 'application/octet-stream'
        }
      } : undefined,

      // Background Check Info
      backgroundCheck: {
        criminalRecord: false,
        creditScore: undefined,
        employmentHistory: [],
        education: [],
        references: []
      },

      // Identity Info
      identityInfo: {
        fullName: basicApplication.applicantName,
        email: apiItem.email,
        phoneNumber: apiItem.phone,
        address: apiItem.address
      },

      // Allow Edit Flags
      allowPILicenseUpdate: apiItem.allowPILicenseUpdate ?? false,
      allowBondUpdate: apiItem.allowBondUpdate ?? false,
    });
  }

  /**
   * Get paginated ghost applications with optional filters
   */
  static async getApplications(
    page: number = 1,
    pageSize: number = 5,
    filter?: GhostVerificationFilter
  ): Promise<PaginatedGhostApplications> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);

      let filteredApplications = mockGhostApplications.map(
        GhostVerificationService.sanitizeGhostApplication
      );

      // Apply status filter
      if (filter?.status && filter.status !== 'all') {
        const statusArray = Array.isArray(filter.status) ? filter.status : [filter.status];
        filteredApplications = filteredApplications.filter(
          (app) => statusArray.includes(app.verificationStatus)
        );
      }

      // Apply search filter
      if (filter?.search) {
        const searchLower = filter.search.toLowerCase();
        filteredApplications = filteredApplications.filter(
          (app) =>
            app.applicantName.toLowerCase().includes(searchLower) ||
            app.ghostId.toLowerCase().includes(searchLower) ||
            app.submittedOn.includes(searchLower)
        );
      }

      const total = filteredApplications.length;
      const totalPages = Math.ceil(total / pageSize);
      const startIndex = (page - 1) * pageSize;
      const data = filteredApplications.slice(startIndex, startIndex + pageSize);

      return {
        data,
        total,
        page,
        pageSize,
        totalPages,
      };
    } else {
      const params: Record<string, string> = {
        page: page.toString(),
        pageSize: pageSize.toString(),
      };

      // Add optional filters
      if (filter?.status && filter.status !== 'all') {
        const statusArray = Array.isArray(filter.status) ? filter.status : [filter.status];
        params.status = statusArray.join(',');
      }
      if (filter?.search) {
        params.keyword = filter.search;
      }

      const response = await apiService.get<{
        isSuccess: boolean;
        data: {
          total: number;
          items: any[];
        };
        message?: string;
      }>('/admin/ghost-verifications', { params });

      if (!response.isSuccess || !response.data) {
        throw new Error(response.message || 'Failed to fetch ghost applications');
      }

      // Convert API response to expected format
      return {
        data: response.data.items.map(GhostVerificationService.convertGhostApplicationFromApi),
        total: response.data.total,
        page,
        pageSize,
        totalPages: Math.ceil(response.data.total / pageSize),
      };
    }
  }

  /**
   * Approve a ghost application
   * @param ghostTier - Required: Junior, Senior, or Elite
   */
  static async approveApplication(id: string, ghostTier: string): Promise<boolean> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      const application = mockGhostApplications.find((app) => app.id === id);
      if (application) {
        application.verificationStatus = 'Approved';
        return true;
      }
      return false;
    } else {
      const response = await apiService.post<{
        isSuccess: boolean;
        message?: string;
      }>(`/admin/ghost-verifications/${id}/verify`, {
        approve: true,
        ghostTier: ghostTier
      });

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to approve application');
      }

      return true;
    }
  }

  /**
   * Reject a ghost application
   * @param rejectionReason - Required: Reason for rejection
   */
  static async rejectApplication(
    id: string,
    rejectionReason: string
  ): Promise<boolean> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      const application = mockGhostApplications.find((app) => app.id === id);
      if (application) {
        application.verificationStatus = 'Rejected';
        return true;
      }
      return false;
    } else {
      const response = await apiService.post<{
        isSuccess: boolean;
        message?: string;
      }>(`/admin/ghost-verifications/${id}/verify`, {
        approve: false,
        note: rejectionReason
      });

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to reject application');
      }

      return true;
    }
  }

  /**
   * Get application by ID
   */
  static async getApplicationById(id: string): Promise<GhostApplication | null> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      const application = mockGhostApplications.find((app) => app.id === id);
      return application
        ? GhostVerificationService.sanitizeGhostApplication(application)
        : null;
    } else {
      const response = await apiService.get<{
        isSuccess: boolean;
        data: any;
        message?: string;
      }>(`/ghost-verification/${id}`);

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to fetch application');
      }

      return response.data
        ? GhostVerificationService.convertGhostApplicationFromApi(response.data)
        : null;
    }
  }

  /**
   * Get application detail by ID
   */
  static async getApplicationDetail(id: string): Promise<GhostApplicationDetail | null> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      const detail = generateGhostApplicationDetail(id);
      if (detail) {
        return GhostVerificationService.sanitizeGhostApplicationDetail(detail);
      }
      // Fallback: try to find basic application
      const application = mockGhostApplications.find((app) => app.id === id);
      if (application) {
        return GhostVerificationService.sanitizeGhostApplicationDetail({
          ...application,
        } as GhostApplicationDetail);
      }
      return null;
    } else {
      const response = await apiService.get<{
        isSuccess: boolean;
        data: any;
        message?: string;
      }>(`/admin/ghost-verifications/${id}`);

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to fetch application detail');
      }

      return response.data ? GhostVerificationService.convertGhostApplicationDetailFromApi(response.data) : null;
    }
  }

  /**
   * Approve bond for a ghost application
   */
  static async approveBond(
    id: string,
    options?: { verificationNotes?: string }
  ): Promise<boolean> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      // In mock mode, update the bond status in the detail
      const detail = generateGhostApplicationDetail(id);
      if (detail && detail.bondInfo) {
        detail.bondInfo.verificationStatus = 'verified';
        detail.bondInfo.verifiedBy = 'Admin User';
        detail.bondInfo.verifiedAt = new Date();
        detail.bondInfo.verifiedAtText = formatDateTime(new Date());
        if (options?.verificationNotes) {
          detail.bondInfo.verificationNotes = options.verificationNotes;
        }
        return true;
      }
      return false;
    } else {
      const response = await apiService.post<{
        isSuccess: boolean;
        message?: string;
      }>(`/ghost-verification/${id}/bond/approve`, {
        verificationNotes: options?.verificationNotes,
      });

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to approve bond');
      }

      return true;
    }
  }

  /**
   * Reject bond for a ghost application
   */
  static async rejectBond(
    id: string,
    options: { rejectionReason: string }
  ): Promise<boolean> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      // In mock mode, update the bond status in the detail
      const detail = generateGhostApplicationDetail(id);
      if (detail && detail.bondInfo) {
        detail.bondInfo.verificationStatus = 'rejected';
        detail.bondInfo.rejectionReason = options.rejectionReason;
        detail.bondInfo.verifiedBy = 'Admin User';
        detail.bondInfo.verifiedAt = new Date();
        detail.bondInfo.verifiedAtText = formatDateTime(new Date());
        return true;
      }
      return false;
    } else {
      const response = await apiService.post<{
        isSuccess: boolean;
        message?: string;
      }>(`/ghost-verification/${id}/bond/reject`, {
        rejectionReason: options.rejectionReason,
      });

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to reject bond');
      }

      return true;
    }
  }

  /**
   * Update bond verification status
   */
  static async updateBondStatus(
    id: string,
    options: {
      verificationStatus: BondVerificationStatus;
      verificationNotes?: string;
    }
  ): Promise<boolean> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      // In mock mode, update the bond status in the detail
      const detail = generateGhostApplicationDetail(id);
      if (detail && detail.bondInfo) {
        detail.bondInfo.verificationStatus = options.verificationStatus;
        if (options.verificationNotes !== undefined) {
          detail.bondInfo.verificationNotes = options.verificationNotes;
        }
        if (options.verificationStatus === 'verified' || options.verificationStatus === 'rejected') {
          detail.bondInfo.verifiedBy = 'Admin User';
          detail.bondInfo.verifiedAt = new Date();
          detail.bondInfo.verifiedAtText = formatDateTime(new Date());
        }
        return true;
      }
      return false;
    } else {
      const response = await apiService.put<{
        isSuccess: boolean;
        message?: string;
      }>(`/ghost-verification/${id}/bond/status`, {
        verificationStatus: options.verificationStatus,
        verificationNotes: options.verificationNotes,
      });

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to update bond status');
      }

      return true;
    }
  }

  /**
   * Allow editing PI License for a ghost verification
   */
  static async allowPILicenseUpdate(id: string): Promise<boolean> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      return true;
    } else {
      const response = await apiService.post<{
        isSuccess: boolean;
        message?: string;
      }>(`/admin/ghost-verifications/${id}/allow-pi-license-update`, {});

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to allow PI License update');
      }

      return true;
    }
  }

  /**
   * Allow editing Bond for a ghost verification
   */
  static async allowBondUpdate(id: string): Promise<boolean> {
    if (IS_MOCK) {
      await delay(MOCK_DELAY);
      return true;
    } else {
      const response = await apiService.post<{
        isSuccess: boolean;
        message?: string;
      }>(`/admin/ghost-verifications/${id}/allow-bond-update`, {});

      if (!response.isSuccess) {
        throw new Error(response.message || 'Failed to allow Bond update');
      }

      return true;
    }
  }
}

export { GhostVerificationService };

